import React, { useState, useMemo } from 'react';

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  name?: string;
  id?: string;
  required?: boolean;
  showStrengthIndicator?: boolean;
  className?: string;
  error?: string;
}

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
  bgColor: string;
  percentage: number;
  feedback: string[];
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  placeholder = 'Enter password',
  name,
  id,
  required = false,
  showStrengthIndicator = false,
  className = '',
  error
}) => {
  const [showPassword, setShowPassword] = useState(false);

  // Calculate password strength
  const passwordStrength = useMemo((): PasswordStrength => {
    if (!value || value.length === 0) {
      return {
        score: 0,
        label: '',
        color: '',
        bgColor: '',
        percentage: 0,
        feedback: []
      };
    }

    let score = 0;
    const feedback: string[] = [];

    // Length check
    if (value.length >= 8) {
      score += 1;
    } else {
      feedback.push('At least 8 characters');
    }

    // Lowercase check
    if (/[a-z]/.test(value)) {
      score += 1;
    } else {
      feedback.push('One lowercase letter');
    }

    // Uppercase check
    if (/[A-Z]/.test(value)) {
      score += 1;
    } else {
      feedback.push('One uppercase letter');
    }

    // Number check
    if (/\d/.test(value)) {
      score += 1;
    } else {
      feedback.push('One number');
    }

    // Special character check
    if (/[@$!%*?&]/.test(value)) {
      score += 1;
    } else {
      feedback.push('One special character (@$!%*?&)');
    }

    // Determine label and color based on score
    let label = '';
    let color = '';
    let bgColor = '';
    let percentage = 0;

    if (score <= 1) {
      label = 'Weak';
      color = 'text-red-600';
      bgColor = 'bg-red-500';
      percentage = 20;
    } else if (score === 2) {
      label = 'Fair';
      color = 'text-orange-600';
      bgColor = 'bg-orange-500';
      percentage = 40;
    } else if (score === 3) {
      label = 'Good';
      color = 'text-yellow-600';
      bgColor = 'bg-yellow-500';
      percentage = 60;
    } else if (score === 4) {
      label = 'Strong';
      color = 'text-green-600';
      bgColor = 'bg-green-500';
      percentage = 80;
    } else if (score === 5) {
      label = 'Very Strong';
      color = 'text-green-700';
      bgColor = 'bg-green-600';
      percentage = 100;
    }

    return { score, label, color, bgColor, percentage, feedback };
  }, [value]);

  return (
    <div className="w-full">
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          name={name}
          id={id}
          required={required}
          className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${className}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            // Eye slash icon (hide)
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
              />
            </svg>
          ) : (
            // Eye icon (show)
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Password strength indicator */}
      {showStrengthIndicator && value && (
        <div className="mt-2 space-y-2">
          {/* Strength bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.bgColor}`}
              style={{ width: `${passwordStrength.percentage}%` }}
            />
          </div>

          {/* Strength label */}
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${passwordStrength.color}`}>
              {passwordStrength.label}
            </span>
            {passwordStrength.feedback.length > 0 && (
              <span className="text-xs text-gray-500">
                {passwordStrength.feedback.length} requirement{passwordStrength.feedback.length !== 1 ? 's' : ''} remaining
              </span>
            )}
          </div>

          {/* Requirements checklist */}
          {passwordStrength.feedback.length > 0 && (
            <div className="text-xs text-gray-600 space-y-1">
              <p className="font-medium">Missing requirements:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {passwordStrength.feedback.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Success message when all requirements met */}
          {passwordStrength.feedback.length === 0 && passwordStrength.score === 5 && (
            <div className="flex items-center gap-1 text-xs text-green-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>All requirements met</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
